import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api";
import { useLocation, useNavigate } from "react-router-dom";
import bgVideo from "../assets/lbg.mp4";

function AttachmentModal({ report, onClose }) {
  if (!report) return null;

  const src = `data:${report.attachment_mime || "image/*"};base64,${
    report.attachment
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-lg border border-white/10 bg-slate-950 p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
          <h3 className="text-lg font-bold text-white">
            {report.attachment_name || "Report Attachment"}
          </h3>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            X
          </button>
        </div>
        {report.attachment_mime === "application/pdf" ? (
          <iframe src={src} title="Report attachment" className="h-[75vh] w-full rounded" />
        ) : (
          <img
            src={src}
            alt="Report attachment"
            className="min-h-0 w-full flex-1 rounded object-contain"
          />
        )}
      </div>
    </div>
  );
}

function EditModal({ report, setReport, onClose, onSave }) {
  if (!report) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-xl rounded-lg border border-white/10 bg-slate-950 p-6 text-white shadow-2xl">
        <div className="mb-5 border-b border-white/10 pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-300">
            Report Status
          </p>
          <h3 className="mt-2 text-2xl font-bold">{report.subject}</h3>
          <p className="mt-1 text-sm text-slate-400">
            Update status and add resolution details when the issue is fixed.
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">
              Status
            </label>
            <select
              value={report.status}
              onChange={(e) => setReport({ ...report, status: e.target.value })}
              className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            >
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">
              Workflow Stage
            </label>
            <select
              value={report.workflow_stage || "new"}
              onChange={(e) => setReport({ ...report, workflow_stage: e.target.value })}
              className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            >
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="verified">Verified</option>
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">
              Target Resolution
            </label>
            <input
              type="datetime-local"
              value={
                report.target_resolution_at
                  ? new Date(report.target_resolution_at).toISOString().slice(0, 16)
                  : ""
              }
              onChange={(e) =>
                setReport({ ...report, target_resolution_at: e.target.value || null })
              }
              className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-200">
              Resolution Notes
            </label>
            <textarea
              rows={4}
              value={report.resolution_notes || ""}
              onChange={(e) =>
                setReport({ ...report, resolution_notes: e.target.value })
              }
              className="w-full resize-none rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
              placeholder="What was done to resolve this report?"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            className="rounded-lg border border-white/15 px-5 py-2 font-semibold text-slate-200 transition hover:bg-white/10"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="rounded-lg bg-teal-600 px-5 py-2 font-semibold text-white transition hover:bg-teal-500"
            onClick={onSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function ArchiveModal({
  report,
  category,
  setCategory,
  note,
  setNote,
  onClose,
  onConfirm,
}) {
  if (!report) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-lg border border-white/10 bg-slate-950 p-6 text-white shadow-2xl">
        <div className="mb-5 border-b border-white/10 pb-4">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-300">
            Archive Report
          </p>
          <h3 className="mt-2 text-2xl font-bold">{report.subject}</h3>
          <p className="mt-1 text-sm text-slate-400">
            Archived reports are hidden from the active list but kept for
            records.
          </p>
        </div>

        <div className="space-y-3">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30"
          >
            <option value="Resolved">Resolved</option>
            <option value="Duplicate">Duplicate</option>
            <option value="Invalid">Invalid</option>
            <option value="Policy">Policy</option>
            <option value="Other">Other</option>
          </select>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            className="w-full resize-none rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-400/30"
            placeholder="Archive note"
          />
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-5 py-2 font-semibold text-slate-200 transition hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="rounded-lg bg-amber-600 px-5 py-2 font-semibold text-white transition hover:bg-amber-500"
          >
            Archive
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ReportsList() {
  const [reports, setReports] = useState([]);
  const [editingReport, setEditingReport] = useState(null);
  const [archiveReport, setArchiveReport] = useState(null);
  const [archiveCategory, setArchiveCategory] = useState("Resolved");
  const [archiveNote, setArchiveNote] = useState("");
  const [attachmentReport, setAttachmentReport] = useState(null);
  const [toast, setToast] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [slaFilter, setSlaFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [undoArchiveId, setUndoArchiveId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchReports = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }
      const res = await api.get("/admin/reports?include_archived=1", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReports(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        localStorage.clear();
        navigate("/");
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    if (!toast) return;
    const timeout = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timeout);
  }, [toast]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusIdRaw = params.get("focus");
    if (!focusIdRaw || reports.length === 0) return;

    const focusId = Number(focusIdRaw);
    if (!Number.isFinite(focusId)) return;

    const report = reports.find((item) => Number(item.id) === focusId);
    if (!report) return;

    const action = params.get("action");

    setStatusFilter(report.archived_at ? "Archived" : "Active");
    setHighlightId(focusId);

    setTimeout(() => {
      const card = document.getElementById(`report-card-${focusId}`);
      if (card) {
        card.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      if (action === "edit" && !report.archived_at) {
        setEditingReport({ ...report });
      }
    }, 120);

    setTimeout(() => setHighlightId(null), 2400);
    navigate("/admin/reports", { replace: true });
  }, [location.search, navigate, reports]);

  const handleSaveEdit = async () => {
    if (!editingReport) return;
    try {
      await api.put(`/admin/reports/${editingReport.id}`, {
        status: editingReport.status,
        workflow_stage: editingReport.workflow_stage || undefined,
        target_resolution_at: editingReport.target_resolution_at || null,
        resolution_notes: editingReport.resolution_notes || "",
      });
      setToast({ message: "Report updated.", type: "success" });
      setEditingReport(null);
      fetchReports();
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to update report.", type: "error" });
    }
  };

  const confirmArchive = async () => {
    if (!archiveReport) return;
    if (!archiveNote.trim()) {
      setToast({ message: "Archive note is required.", type: "error" });
      return;
    }

    try {
      await api.put(`/admin/reports/${archiveReport.id}/archive`, {
        archive_reason_category: archiveCategory,
        archive_reason_note: archiveNote,
      });
      setToast({ message: "Report archived. Undo is available for 8 seconds.", type: "success" });
      setUndoArchiveId(archiveReport.id);
      setTimeout(() => setUndoArchiveId(null), 8000);
      setArchiveReport(null);
      setArchiveCategory("Resolved");
      setArchiveNote("");
      fetchReports();
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to archive report.", type: "error" });
    }
  };

  const handleUndoArchive = async () => {
    if (!undoArchiveId) return;
    try {
      await api.put(`/admin/reports/${undoArchiveId}/restore`);
      setUndoArchiveId(null);
      setToast({ message: "Archive undone. Report restored.", type: "success" });
      fetchReports();
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to undo archive.", type: "error" });
    }
  };

  const handleRestore = async (id) => {
    try {
      await api.put(`/admin/reports/${id}/restore`);
      setToast({ message: "Report restored.", type: "success" });
      fetchReports();
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to restore report.", type: "error" });
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await api.get("/admin/operations/export/reports", {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `reports-export-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setToast({ message: "Failed to export reports.", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  const statusBadge = (status) => {
    if (status === "Resolved") return "border-teal-300/40 bg-teal-500/15 text-teal-200";
    if (status === "In Progress") return "border-sky-300/40 bg-sky-500/15 text-sky-200";
    return "border-amber-300/40 bg-amber-500/15 text-amber-200";
  };

  const slaClass = (status) => {
    if (status === "overdue") return "border-red-300/40 bg-red-500/15 text-red-200";
    if (status === "due_soon") return "border-amber-300/40 bg-amber-500/15 text-amber-200";
    if (status === "resolved") return "border-teal-300/40 bg-teal-500/15 text-teal-200";
    return "border-white/20 bg-white/5 text-slate-300";
  };

  const slaLabel = (status) => {
    if (status === "due_soon") return "Due Soon";
    if (status === "overdue") return "Overdue";
    if (status === "resolved") return "Resolved";
    return "On Track";
  };

  const formatSlaRemaining = (minutes) => {
    if (minutes === null || minutes === undefined) return "-";
    const absMinutes = Math.abs(Number(minutes));
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    if (Number(minutes) < 0) return `${hours}h ${mins}m overdue`;
    return `${hours}h ${mins}m remaining`;
  };

  const filteredReports = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return reports.filter((report) => {
      const archived = Boolean(report.archived_at);
      const matchesStatus =
        statusFilter === "Active"
          ? !archived
          : statusFilter === "Archived"
          ? archived
          : report.status === statusFilter && !archived;
      const matchesSla = slaFilter === "all" || report.sla_status === slaFilter;
      const matchesSearch =
        !query ||
        [
          report.tenant_name,
          report.subject,
          report.message,
          report.category,
          report.priority,
          report.status,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      return matchesStatus && matchesSla && matchesSearch;
    });
  }, [reports, searchTerm, statusFilter, slaFilter]);

  const activeReports = reports.filter((r) => !r.archived_at);
  const dueSoonReports = activeReports.filter((r) => r.sla_status === "due_soon");
  const overdueReports = activeReports.filter((r) => r.sla_status === "overdue");

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 font-sans">
      <div className="absolute inset-0">
        <video data-workspace-background="true" aria-hidden="true"
          className="h-full w-full object-cover"
          src={bgVideo}
          autoPlay
          muted
          loop
          playsInline
        />
        <div className="absolute inset-0 bg-slate-950/85"></div>
      </div>

      <AttachmentModal
        report={attachmentReport}
        onClose={() => setAttachmentReport(null)}
      />
      <EditModal
        report={editingReport}
        setReport={setEditingReport}
        onClose={() => setEditingReport(null)}
        onSave={handleSaveEdit}
      />
      <ArchiveModal
        report={archiveReport}
        category={archiveCategory}
        setCategory={setArchiveCategory}
        note={archiveNote}
        setNote={setArchiveNote}
        onClose={() => {
          setArchiveReport(null);
          setArchiveCategory("Resolved");
          setArchiveNote("");
        }}
        onConfirm={confirmArchive}
      />

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-6 text-white sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Admin Reports
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide text-white md:text-4xl">
              Report Workflow
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              Prioritize, resolve, archive, and audit tenant maintenance
              reports.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-center lg:grid-cols-4">
            {[
              ["Active", activeReports.length, "text-white"],
              ["Due Soon", dueSoonReports.length, "text-amber-200"],
              ["Overdue", overdueReports.length, "text-red-200"],
            ].map(([label, value, color]) => (
              <div
                key={label}
                className="rounded-lg border border-white/10 bg-white/5 px-4 py-3"
              >
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4 flex justify-end">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="rounded-lg border border-white/15 px-4 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {exporting ? "Exporting..." : "Export Reports Excel"}
          </button>
        </div>

        <div className="mb-5 grid gap-3 rounded-lg border border-white/10 bg-slate-950/90 p-4 shadow-xl xl:grid-cols-[minmax(0,1fr)_auto_auto] xl:items-center">
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-white/10 bg-white px-3 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30"
            placeholder="Search tenant, subject, category or message"
          />

          <div className="flex flex-wrap gap-2">
            {["Active", "Pending", "In Progress", "Resolved", "Archived"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  statusFilter === status
                    ? "bg-teal-600 text-white"
                    : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
                }`}
              >
                {status}
              </button>
            ))}
          </div>

        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          {["all", "on_track", "due_soon", "overdue", "resolved"].map((status) => (
            <button
              key={status}
              onClick={() => setSlaFilter(status)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                slaFilter === status
                  ? "bg-teal-600 text-white"
                  : "border border-white/10 bg-white/5 text-slate-200 hover:bg-white/10"
              }`}
            >
              {slaLabel(status)}
            </button>
          ))}
        </div>

        {filteredReports.length === 0 ? (
          <p className="rounded-lg border border-dashed border-white/15 py-8 text-center text-slate-300">
            No reports found.
          </p>
        ) : (
          <div className="grid gap-4">
            {filteredReports.map((r) => (
              <article
                key={r.id}
                id={`report-card-${r.id}`}
                className={`rounded-lg border bg-slate-950/90 p-5 shadow-2xl ${
                  highlightId === r.id
                    ? "border-teal-300/70 ring-1 ring-teal-300/45"
                    : "border-white/10"
                }`}
              >
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div>
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">
                        {r.category || "Other"}
                      </span>
                      <span
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusBadge(
                          r.status
                        )}`}
                      >
                        {r.status}
                      </span>
                      <span className="rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold text-slate-300">
                        Stage: {String(r.workflow_stage === "assigned" ? "in_progress" : (r.workflow_stage || "new")).replaceAll("_", " ")}
                      </span>
                      <span
                        className={`rounded-full border px-3 py-1 text-xs font-semibold ${slaClass(
                          r.sla_status
                        )}`}
                      >
                        SLA: {slaLabel(r.sla_status)}
                      </span>
                      {r.escalation_stage?.startsWith("urgent_") && (
                        <span className="rounded-full border border-red-300/40 bg-red-500/15 px-3 py-1 text-xs font-semibold text-red-200">
                          Escalation: {r.escalation_stage.replace("urgent_", "").replace("h", "h+")}
                        </span>
                      )}
                      {r.archived_at && (
                        <span className="rounded-full border border-amber-300/40 bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-200">
                          Archived
                        </span>
                      )}
                    </div>
                    <h2 className="mt-3 text-2xl font-bold text-white">
                      {r.subject}
                    </h2>
                    <p className="mt-1 text-sm text-slate-400">
                      {r.tenant_name || "Unknown Tenant"} - Submitted{" "}
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {r.attachment && (
                      <>
                        <button
                          onClick={() => setAttachmentReport(r)}
                          className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-slate-100 transition hover:border-teal-300/50 hover:bg-teal-500/10"
                        >
                          Attachment
                        </button>
                        <span
                          className={`inline-flex rounded-full border px-3 py-2 text-xs font-semibold ${
                            r.attachment_integrity === "verified"
                              ? "border-teal-300/40 bg-teal-500/15 text-teal-200"
                              : r.attachment_integrity === "tampered"
                              ? "border-red-300/40 bg-red-500/15 text-red-200"
                              : "border-amber-300/40 bg-amber-500/15 text-amber-200"
                          }`}
                        >
                          Attachment {r.attachment_integrity || "unknown"}
                        </span>
                      </>
                    )}
                    {!r.archived_at ? (
                      <>
                        <button
                          className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                          onClick={() => setEditingReport({ ...r })}
                        >
                          Update
                        </button>
                        <button
                          onClick={() => {
                            setArchiveCategory("Resolved");
                            setArchiveNote("");
                            setArchiveReport(r);
                          }}
                          className="rounded-lg border border-amber-300/30 bg-amber-500/15 px-3 py-2 text-sm font-semibold text-amber-100 transition hover:bg-amber-500/25"
                        >
                          Archive
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => handleRestore(r.id)}
                        className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-teal-500"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>

                <p className="mt-4 whitespace-pre-wrap rounded-lg bg-white/5 p-4 text-sm leading-6 text-slate-300">
                  {r.message}
                </p>

                <div className="mt-4 grid gap-3 text-sm md:grid-cols-3 xl:grid-cols-6">
                  {[
                    ["In Progress", r.in_progress_at],
                    ["Resolved", r.resolved_at],
                    ["Verified", r.verified_at],
                    ["Archived", r.archived_at],
                    ["SLA Deadline", r.sla_deadline_at],
                    ["Target Resolution", r.target_resolution_at],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      className="rounded-lg border border-white/10 bg-white/5 p-3"
                    >
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        {label}
                      </p>
                      <p className="mt-1 text-slate-200">
                        {value ? new Date(value).toLocaleString() : "-"}
                      </p>
                    </div>
                  ))}
                </div>

                {!r.archived_at && r.status !== "Resolved" && (
                  <div className="mt-3 rounded-lg border border-white/10 bg-white/5 p-3 text-sm text-slate-300">
                    {formatSlaRemaining(r.sla_remaining_minutes)}
                  </div>
                )}

                {(r.resolution_notes || r.archive_reason || r.archive_reason_note || r.archive_reason_category) && (
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    {r.resolution_notes && (
                      <div className="rounded-lg border border-teal-300/20 bg-teal-500/10 p-4 text-sm text-teal-100">
                        <p className="font-semibold">Resolution Notes</p>
                        <p className="mt-1 whitespace-pre-wrap">
                          {r.resolution_notes}
                        </p>
                      </div>
                    )}
                    {(r.archive_reason || r.archive_reason_note || r.archive_reason_category) && (
                      <div className="rounded-lg border border-amber-300/20 bg-amber-500/10 p-4 text-sm text-amber-100">
                        <p className="font-semibold">Archive Reason</p>
                        <p className="mt-1 whitespace-pre-wrap">{r.archive_reason || "-"}</p>
                        {r.archive_reason_category && (
                          <p className="mt-1 text-xs">Category: {r.archive_reason_category}</p>
                        )}
                        {r.archive_reason_note && (
                          <p className="mt-1 whitespace-pre-wrap">{r.archive_reason_note}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {toast && (
          <div
            className={`fixed right-4 top-4 z-50 rounded-lg px-5 py-3 text-white shadow-2xl ${
              toast.type === "success" ? "bg-teal-600" : "bg-red-600"
            }`}
          >
            <div className="flex items-center gap-3">
              <span>{toast.message}</span>
              {undoArchiveId && (
                <button
                  onClick={handleUndoArchive}
                  className="rounded border border-white/30 px-2 py-1 text-xs font-semibold hover:bg-white/10"
                >
                  Undo
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
